// src/components/UserMemoryPanel.js
import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import {
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { isDarkMode, themeColors } from "../../utils/constants";

/* ---------------- GraphQL ---------------- */

async function fetchUserMemoryProfile() {
  const body = {
    query: `
      mutation UserMemoryProfile {
        userMemoryProfile {
          custom
          settings
          fields
        }
      }
    `,
  };
  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors.map(e => e.message).join(", "));
  return json.data.userMemoryProfile;
}

async function updateUserMemoryProfile({ custom, settings, fields, replace = true }) {
  const body = {
    query: `
      mutation UpdateUserMemoryProfile($custom: String, $settings: JSON, $fields: JSON, $replace: Boolean) {
        updateUserMemoryProfile(custom: $custom, settings: $settings, fields: $fields, replace: $replace) {
          custom
          settings
          fields
        }
      }
    `,
    variables: { custom, settings, fields, replace },
  };
  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors.map(e => e.message).join(", "));
  return json.data.updateUserMemoryProfile;
}

/* ---------------- Fields list: mirror Ruby PROFILE_FIELDS ---------------- */

const PROFILE_FIELD_KEYS = [
  "trading_style",
  "risk_tolerance",
  "trading_products",
  "recent_topics",
  "investment_goals",
  "markets_followed",
  "notable_preferences",
  "time_horizon",
  "preferred_indicators",
  "strategy_type",

  "decision_speed",
  "confidence_level",
  "learning_style",
  "emotional_profile",
  "favorite_assets",
  "typical_trade_duration",
  "pain_points",
  "goal_motivation",
  "communication_tone_preference",
  "risk_management_focus",

  "general_personality_traits",
  "preferred_response_length",
  "user_prompts",
  "active_positions",
  "recent_positions",
  "past_positions",
  "past_topics",
  "general_information",
];

const toLabel = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const DEFAULT_SETTINGS = { charting: { show_charts_by_default: true } };

/* ---------------- Visuals ---------------- */

const textFieldStyles = {
  "& .MuiInputBase-input": { color: themeColors.color },
  "& .MuiFormLabel-root": { color: themeColors.color },
  "& .MuiOutlinedInput-root": {
    color: themeColors.color,
    "& fieldset": { borderColor: themeColors.color },
    "&:hover fieldset": { borderColor: themeColors.color },
    "&.Mui-focused fieldset": { borderColor: themeColors.color },
  },
};

// Spacing for fixed footer and for keeping last inputs visible
const FIXED_BOTTOM_GAP = 82;//58; // Footer height
const SCROLLER_BOTTOM_PAD = 20;

export default function UserMemoryPanel({ setActivePanel }) {
  const [data, setData] = useState({ custom: "", settings: DEFAULT_SETTINGS, fields: {} });
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasSaved, setHasSaved] = useState(false);

  const outerRef = useRef(null);
  const fieldsRef = useRef(null);
  const [fieldsHeight, setFieldsHeight] = useState(null);

  // Measure remaining viewport space for the scroller.
  useLayoutEffect(() => {
    const measure = () => {
      if (!fieldsRef.current) return;
      const scrollerTop = fieldsRef.current.getBoundingClientRect().top;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const available = vh - scrollerTop - FIXED_BOTTOM_GAP - 8;
      setFieldsHeight(Math.max(140, available));
    };

    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    const ro = new ResizeObserver(measure);
    if (outerRef.current) ro.observe(outerRef.current);
    if (fieldsRef.current) ro.observe(fieldsRef.current);

    const raf = requestAnimationFrame(measure);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      ro.disconnect();
    };
  }, [loading]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const server = await fetchUserMemoryProfile();
        if (!cancelled && server) {
          const normalizedFields = {};
          PROFILE_FIELD_KEYS.forEach((k) => {
            const v = server.fields?.[k];
            normalizedFields[k] = Array.isArray(v)
              ? v
              : (typeof v === "string" && v.length ? v.split("|").map(s => s.trim()) : []);
          });
          setData({
            custom: server.custom ?? "",
            settings: server.settings ?? DEFAULT_SETTINGS,
            fields: normalizedFields,
          });
          setDirty(false);
        }
      } catch (e) {
        console.error("fetchUserMemoryProfile error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    const sanitized = {
      custom: data.custom ?? "",
      settings: data.settings ?? {},
      fields: Object.fromEntries(
        Object.entries(data.fields || {}).map(([k, arr]) => [
          k,
          (Array.isArray(arr) ? arr : String(arr).split("|"))
            .map((s) => (typeof s === "string" ? s.trim() : s))
            .filter((s) => s && String(s).length > 0),
        ])
      ),
    };
    try {
      const saved = await updateUserMemoryProfile({ ...sanitized, replace: true });
      setData(saved);
      setDirty(false);
      setHasSaved(true);
    } catch (e) {
      console.error("updateUserMemoryProfile error:", e);
    }
  };

  const handleReset = () => {
    setData({ custom: "", settings: DEFAULT_SETTINGS, fields: PROFILE_FIELD_KEYS.reduce((acc,k)=>{acc[k]=[];return acc;}, {}) });
    setDirty(true);
  };

  const setCustom = (v) => {
    setData((prev) => ({ ...prev, custom: v }));
    setDirty(true);
  };

  const setSetting = (path, v) => {
    const parts = path.split(".");
    setData((prev) => {
      const next = { ...prev, settings: { ...prev.settings } };
      let leaf = next.settings;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        leaf[p] = leaf[p] ? { ...leaf[p] } : {};
        leaf = leaf[p];
      }
      leaf[parts[parts.length - 1]] = v;
      return next;
    });
    setDirty(true);
  };

  const updateFieldArray = (fieldKey, updater) => {
    setData((prev) => {
      const cur = Array.isArray(prev.fields?.[fieldKey]) ? prev.fields[fieldKey] : [];
      const nextArr = updater(cur);
      return { ...prev, fields: { ...prev.fields, [fieldKey]: nextArr } };
    });
    setDirty(true);
  };

  // Clean, high-contrast neutrals
  const textColor    = isDarkMode ? "#E8EAED" : "#1D1D1F";
  const subTextColor = isDarkMode ? "#9AA0A6" : "#6B7280";
  const cardBg       = isDarkMode ? "#151A20" : "#EBEEF3";
  const fieldBg      = isDarkMode ? "#0F141A" : "#EBEEF3";
  const dividerColor = isDarkMode ? "#2A3240" : "#E5E7EB";
  const itemBg       = isDarkMode ? "#11161C" : "#EBEEF3";
  const itemBorder   = isDarkMode ? "#2A3240" : "transparent";

  return (
    <Box
      ref={outerRef}
      sx={{
        position: "relative", // anchor the fixed footer inside this panel
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        px: 2,
        pt: 0.5,
        mt: 0,
        pb: `${FIXED_BOTTOM_GAP}px`, // make space for footer
      }}
    >
      {/* Header + Actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 1, pb: 1, position: 'relative' }}>
        <Box sx={{ mt: 0, mb: 0.5, position: 'absolute', top: '50px', right: '5px'}}>
          <Typography variant="caption" sx={{ mb: 1, pb: 1, ml: 1, color: subTextColor }}>
            {dirty ? "Unsaved changes" : (hasSaved ? "All changes saved." : "")}
          </Typography>
        </Box>

        <Box sx={{ mt: 0, gap: 0.5}}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={!dirty || loading}
              sx={{ textTransform: "none" }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2, borderColor: dividerColor }} />

      {/* 1) Custom Information / Prompts */}
      <SectionTitle title="Custom Information" />
      <Typography variant="body2" sx={{ mb: 1, color: subTextColor }}>
        Tell Incite about yourself.
      </Typography>

      <TextField
        value={data.custom}
        onChange={(e) => setCustom(e.target.value)}
        multiline
        minRows={4}
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          ...textFieldStyles,
          mb: 0,
          "& .MuiInputBase-root": {
            backgroundColor: fieldBg,
            borderRadius: 1.25,
            fontSize: 13,
          },
        }}
      />

      <Divider sx={{ my: 2, borderColor: dividerColor }} />

      {/* 2) Settings */}
      <SectionTitle title="Settings" />
      <Typography variant="body2" sx={{ mb: 1, color: subTextColor }}>
        Incite will also adjust autonomously.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0, mb: 0 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!data.settings?.charting?.show_charts_by_default}
              onChange={(e) => setSetting("charting.show_charts_by_default", e.target.checked)}
            />
          }
          label={
            <span style={{ fontSize: 13, color: textColor, fontWeight: 600 }}>
              Show Charts by Default
            </span>
          }
        />
      </Box>

      <Divider sx={{ my: 2, borderColor: dividerColor }} />

      {/* 3) Fields scroller — dynamically sized to remaining viewport */}
      <SectionTitle title="Fields" />
      <Typography variant="body2" sx={{ mb: 1, color: subTextColor }}>
        AI-generated personal preferences and profile info.
      </Typography>

      <Box
        ref={fieldsRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          overflowY: "auto",
          height: fieldsHeight ? `${fieldsHeight}px` : "auto",
          pr: 1,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.4)", borderRadius: 4 },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          pb: `${SCROLLER_BOTTOM_PAD}px`,
          scrollPaddingBottom: `${SCROLLER_BOTTOM_PAD}px`,
        }}
      >
        {PROFILE_FIELD_KEYS.map((fieldKey) => {
          const label = toLabel(fieldKey);
          const items = Array.isArray(data.fields?.[fieldKey]) ? data.fields[fieldKey] : [];

          return (
            <Accordion
              key={fieldKey}
              disableGutters
              elevation={0}
              sx={{
                background: cardBg,
                border: `1px solid ${dividerColor}`,
                borderRadius: "10px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: themeColors.color }} />}
                sx={{ "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 } }}
              >
                <Typography sx={{ color: textColor, fontWeight: 600, fontSize: 13 }}>
                  {label}
                </Typography>
                <Chip
                  label={items.length}
                  size="small"
                  sx={{
                    height: 20,
                    color: textColor,
                    borderColor: dividerColor,
                    borderWidth: 1,
                    borderStyle: "solid",
                    background: "transparent",
                  }}
                />
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {items.length === 0 && (
                    <Typography variant="caption" sx={{ color: subTextColor, ml: 0.5 }}>
                      (No entries yet)
                    </Typography>
                  )}

                  {items.map((val, idx) => (
                    <Box
                      key={`${fieldKey}-${idx}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: itemBg,
                        border: `1px solid ${itemBorder}`,
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.75,
                        transition: "background-color 120ms ease, border-color 120ms ease",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#131922" : "#F9FAFB",
                          borderColor: isDarkMode ? "#354057" : "#D1D5DB",
                        },
                      }}
                    >
                      <TextField
                        value={val}
                        onChange={(e) =>
                          updateFieldArray(fieldKey, (arr) => {
                            const next = [...arr];
                            next[idx] = e.target.value;
                            return next;
                          })
                        }
                        size="small"
                        fullWidth
                        placeholder=""
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          ...textFieldStyles,
                          "& .MuiInputBase-root": {
                            backgroundColor: fieldBg,
                            borderRadius: 1,
                            fontSize: 13,
                          },
                        }}
                      />
                      <IconButton
                        aria-label="remove"
                        onClick={() =>
                          updateFieldArray(fieldKey, (arr) => arr.filter((_, i) => i !== idx))
                        }
                        size="small"
                      >
                        <DeleteOutlineIcon sx={{ color: themeColors.color }} />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    startIcon={<AddIcon />}
                    variant="text"
                    size="small"
                    onClick={() => updateFieldArray(fieldKey, (arr) => [...arr, ""])}
                    sx={{ alignSelf: "flex-start", textTransform: "none" }}
                  >
                    Add item
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Fixed Privacy Footer */}
      <Box
        className="base-text"
        sx={{
          position: "absolute",//"absolute",
          left: 0,
          bottom: 68,
          width: '100%',
          height: '22px',
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0, margin: 0,
          color: subTextColor,
          backgroundColor: isDarkMode ? "#222222" : "#E9E9E9",
          fontSize: 11,
        }}
      >
        {/* <Typography variant="caption" sx={{ 
          color: subTextColor, textAlign: "center",
          padding: 0, margin: 0,
          backgroundColor: 'red',
        }}> */}
          Privacy: user information is held strictly private and never disclosed externally.
        {/* </Typography> */}
      </Box>
    </Box>
  );
}

/* ---------------- small helper ---------------- */

function SectionTitle({ title }) {
  return (
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: 14,
        mb: 0.75,
        color: isDarkMode ? "#E8EAED" : "#1D1D1F",
      }}
    >
      {title}
    </Typography>
  );
}
